import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import useProtectAction from '@/hooks/useProtectAction';
import useStateHandler from "@/hooks/useStateHandler";
import GalleryService from '@/services/gallery.service';
import LikeButton from '@/shared-components/buttons/components/LikeButton';
import UserProfileImage from '@/shared-components/cards/components/UserProfileImage';
import BoardContentRenderer from '@/shared-components/forms/add-board-content/board-text-body/BoardContentRenderer';
import ThoughtIcon from '@/shared-components/icons/components/artwork/ThoughtIcon';
import ViewsIcon from '@/shared-components/icons/components/user-profile/ViewsIcon';
import Utils from '@/utils';
import {
  getPersistedLikeState,
  persistLikeState,
} from '@/utils/likePersistence';
import { assignLocalizedPublicPath } from '@/utils/localePublicUrl';
import {
  hydratePollsWithPersistedVotes,
  persistPollVote,
} from '@/utils/pollPersistence';
import {
  hydrateQuizzesWithPersistedAnswers,
  persistQuizAnswer,
} from '@/utils/quizPersistence';

import PostComments from "./PostComments";

import './PostInfo.css';

// ——— New: standalone header for posts ———
export function PostHeader({ fetchData }) {
  // pick the right owner
  const owner = fetchData.gallery || fetchData.artist || fetchData.collector;
  const timeAgo = Utils.elapsedTimeOrShortDate(new Date(fetchData.createdAt).getTime(), 7);
  return (
    <div className="post__header">
      <UserProfileImage
        image={owner?.user_image?.image_id}
        typeOfProfile={owner?.user_type}
        className="post__header-avatar"
        onClick={() => {
          assignLocalizedPublicPath(`/user/${owner?.user_username}`);
        }}
      />

      <div className="post__header-center">
        <span className="post__header-username">
          {owner?.user_username}
        </span>
        <span className="post__header-views">
          {Utils.numberParserMillionThousand(fetchData.post_visits)}
          <ViewsIcon className="post__header-views-icon" />
        </span>
      </div>

      <div className="post__header-date">
        <span className="post__header-timestamp">
          {timeAgo}
        </span>
      </div>
    </div>
  );
}

export default function PostInfo({ fetchData }) {
  const location = useLocation();
  const { cacheHandler, stateHandler } = useStateHandler();
  const privateActionHandler = useProtectAction({ typeOfProtection: 'USER_ACCOUNT_REQUIRED' });

  const {
    post_structure = [],
    post_texts     = [],
    post_pictures  = [],
    post_artworks  = [],
    post_videos    = [],
    post_polls     = [],
    post_quizzes   = []
  } = fetchData.post_container || {};

  const owner = fetchData.gallery || fetchData.artist || fetchData.collector;
  const [polls, setPolls]     = React.useState(() =>
    hydratePollsWithPersistedVotes(fetchData._id, post_polls)
  );
  const [quizzes, setQuizzes] = React.useState(() =>
    hydrateQuizzesWithPersistedAnswers(fetchData._id, post_quizzes)
  );
  React.useEffect(() => {
    setPolls(hydratePollsWithPersistedVotes(fetchData._id, post_polls));
  }, [fetchData._id, post_polls]);
  React.useEffect(() => {
    setQuizzes(hydrateQuizzesWithPersistedAnswers(fetchData._id, post_quizzes));
  }, [fetchData._id, post_quizzes]);
  // open comments when navigation explicitly requests it
  const [showCommentForm, setShowCommentForm] = useState(location.state?.scrollToComments || false);

  // ─── NEW: keep track of “like count”
  // If the user navigated here from PostListItem, location.state.likesCount will be defined.
  // Otherwise, fall back to fetchData.post_like_count (or 0).
  const persistedLike = getPersistedLikeState(fetchData._id);
  const initialLikeCount = persistedLike?.likeCount ?? (fetchData.post_like_count || 0);
  const [likeCount, setLikeCount] = useState(initialLikeCount);

  // ─── START: guarded like state ─────────────────────────────────────────
  const [isLikedState, setIsLikedState] = useState(
    persistedLike?.isLiked ?? fetchData.is_liked
  );

  useEffect(() => {
    const restored = getPersistedLikeState(fetchData._id);
    if (restored) {
      setLikeCount(restored.likeCount ?? 0);
      setIsLikedState(restored.isLiked ?? false);
    } else {
      const fallbackCount = fetchData.post_like_count || 0;
      const fallbackLiked = fetchData.is_liked ?? false;
      setLikeCount(fallbackCount);
      setIsLikedState(fallbackLiked);
      persistLikeState(fetchData._id, {
        isLiked: fallbackLiked,
        likeCount: fallbackCount,
      });
    }
  }, [fetchData._id, fetchData.post_like_count, fetchData.is_liked]);

  const updatePostCache = (handler) => {
    // we change the valuee of the cache
    const cacheKey = `${owner.id || owner._id}_posts`; 
    const currentPosts = cacheHandler.getCacheValue(cacheKey);
    if (currentPosts) {
      // update the like count in the cached posts
      const updatedPosts = {
        ...currentPosts,
        data: currentPosts.data.data.map(handler),
      };
      cacheHandler.storeInCache(cacheKey, updatedPosts);
    }
  };

  const handleLike = async newStatus => {
    let updatedCount;
    try {
      if (newStatus) {
        updatedCount = likeCount + 1;
      } else {
        updatedCount = likeCount > 0 ? likeCount - 1 : 0;
      }

      setLikeCount(updatedCount);
      setIsLikedState(newStatus);
      persistLikeState(fetchData._id, {
        isLiked: newStatus,
        likeCount: updatedCount,
      });
    } catch (err) {
      console.error("PostInfo like error", err);
    } finally { 
      updatePostCache(
        post => {
          if (post._id === fetchData._id) {
            return { ...post, post_like_count: updatedCount, is_liked: newStatus };
          }
          return post;
        }
      );
    }
  };
  // ─── END: guarded like state ────────────────────────────────────────────

  const [commentsCount, setCommentsCount] = useState(fetchData.comment_count);

  const textsById = React.useMemo(
    () => Object.fromEntries(post_texts.map(t => [t.textId,   t])),
    [post_texts]
  );
  const picturesById = React.useMemo(
    () => Object.fromEntries(post_pictures.map(q => [q.groupId, q])),
    [post_pictures]
  );
    // new: group into arrays so each block.id maps to an array of artworks
  const artworksById = React.useMemo(
    () => Object.fromEntries(post_artworks.map(q => [q.groupId, q])),
    [post_artworks]
  );
  const videosById = React.useMemo(
    () => Object.fromEntries(post_videos.map(q => [q.groupId, q])),
    [post_videos]
  );

  const pollsById = React.useMemo(
    () => Object.fromEntries(polls.map(p => [p.pollId, p])),
    [polls]
  );
  const quizzesById = React.useMemo(
    () => Object.fromEntries(quizzes.map(q => [q.quizId, q])),
    [quizzes]
  );

  const castVote = async (pollId, optionIndex) => {
    const updated = await GalleryService.castPollVote(
      fetchData._id,
      pollId,
      optionIndex
    );
    const withVote = {
      ...updated,
      hasVoted: true,
      userAnswer: optionIndex,
    };
    persistPollVote(fetchData._id, pollId, optionIndex);
    setPolls(ps => ps.map(p =>
      p.pollId === pollId ? withVote : p
    ));
    return withVote.options;
  };

  const castAnswer = async (quizId, answerIndex) => {
    await GalleryService.castQuizAnswer(
      fetchData._id,
      quizId,
      answerIndex
    );
    setQuizzes(qs =>
      qs.map(q =>
        q.quizId === quizId
          // record both that they've answered AND which answer they picked
          ? { ...q, hasAnswered: true, userAnswer: answerIndex }
          : q
      )
    );
    persistQuizAnswer(fetchData._id, quizId, answerIndex);
  };

  const onCommentAdded = () => {
    const newComments = commentsCount + 1;
    setCommentsCount(newComments);

    updatePostCache(
      post => {
        if (post._id === fetchData._id) {
          return { ...post, comment_count: newComments };
        }
        return post;
      }
    );
  };

  return (
    <>
      {/* ——— Post Header: Avatar / Username / Timestamp ——— */}
      <PostHeader fetchData={fetchData} />

      {/* ——— Title & Short Description ——— */}
      <h1 className="post__title">{fetchData.post_container.post_title}</h1>
      {fetchData.post_container.post_description && (
        <p className="post__short-description">
          {fetchData.post_container.post_description}
        </p>
      )}

      {/* ——— Main Content Loop via shared renderer ——— */}
      <BoardContentRenderer
        structure={post_structure}
        postId={fetchData._id}
        textsById={textsById}
        picturesById={picturesById}
        artworksById={artworksById}
        videosById={videosById}
        pollsById={pollsById}
        quizzesById={quizzesById}
        onVote={castVote}
        onAnswer={castAnswer}
        previewMode={true}      // published view
        interactive={true}      // allow poll voting in posts
        postMode={true}
      />
      
      {/* ——— Post Footer: Like button bottom-right ——— */}
      <div className="post__footer">
        <ThoughtIcon
          className="post__footer-comment-icon"
          onClick={(e) => {
            e.stopPropagation();
            stateHandler.set(
              'unregisteredUser',
              commentsCount > 0
                ? 'guest_post_comment_view'
                : 'guest_post_comment_add'
            );
            privateActionHandler(() => setShowCommentForm(v => !v));
          }}
        />
        {commentsCount > 0 && (
          <span className="post__footer-like-count">
            {Utils.numberParserMillionThousand(commentsCount)}
          </span>
        )}
        <LikeButton
          itemId={fetchData._id}
          typeOfItem="POST"
          isLiked={isLikedState}
          onLikeAction={handleLike}
          className="post__footer-like-button"
        />
        {/*
          ─── SHOW THE COUNT─ (conditionally render only if > 0) ──────────
          By now, `likeCount` either came from location.state.likesCount
          (because you “liked” in the list) or from fetchData.post_like_count.
        */}
        {likeCount > 0 && (
          <span className="post__footer-like-count">
            {Utils.numberParserMillionThousand(likeCount)}
          </span>
        )}
      </div>

      {showCommentForm && (
        <div
          className="post__comments-section"
          id="comments-section"
        >
          <PostComments 
            postId={fetchData._id} 
            onCommentAdded={onCommentAdded}
            scrollToComments={showCommentForm}
            isFirstComment={commentsCount === 0}
          />
        </div>
      )}
    </>
  );
}
